import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient.js';

const MoveDayModal = ({ routeId, dayNumber, jobCount, onConfirm, onClose }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [targetDay, setTargetDay] = useState(1);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    const fetchRoutes = async () => {
      // Get available routes (draft/active, excluding current)
      const { data } = await supabase
        .from('routes')
        .select('id, route_name, corridor, start_date, status')
        .in('status', ['draft', 'active'])
        .neq('id', routeId)
        .order('start_date', { ascending: true });

      // Get max day_number per route for "append" suggestion
      const { data: itemData } = await supabase
        .from('route_items')
        .select('route_id, day_number');

      const maxDays = {};
      for (const item of (itemData || [])) {
        if (!maxDays[item.route_id] || item.day_number > maxDays[item.route_id]) {
          maxDays[item.route_id] = item.day_number;
        }
      }

      setRoutes((data || []).map(r => ({ ...r, maxDay: maxDays[r.id] || 0 })));
      setLoading(false);
    };
    fetchRoutes();
  }, [routeId]);

  const handleSelectRoute = (id) => {
    setSelectedRouteId(id);
    const route = routes.find(r => r.id === id);
    // Default to appending after the last day
    setTargetDay((route?.maxDay || 0) + 1);
  };

  const handleMove = () => {
    if (!selectedRouteId) return;
    setMoving(true);
    onConfirm(selectedRouteId, targetDay);
  };

  const selectedRoute = routes.find(r => r.id === selectedRouteId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-white mb-1">
          Move Day {dayNumber} to Another Route
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {jobCount} job{jobCount !== 1 ? 's' : ''} will be moved. Travel rows will be deleted and need recalculating on the target route.
          Remaining days on this route will be renumbered to close the gap.
        </p>

        {loading ? (
          <p className="text-gray-400 text-sm py-4">Loading routes...</p>
        ) : routes.length === 0 ? (
          <p className="text-gray-400 text-sm py-4">No other draft or active routes available.</p>
        ) : (
          <div className="space-y-3">
            {/* Route selection */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Target Route</label>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {routes.map(r => (
                  <button
                    key={r.id}
                    onClick={() => handleSelectRoute(r.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedRouteId === r.id
                        ? 'bg-green-600/20 border border-green-600/40 text-white'
                        : 'bg-gray-900/50 border border-gray-500/30 text-gray-300 hover:bg-gray-900/80'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.route_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        r.status === 'active' ? 'bg-green-600 text-white' : 'bg-gray-500 text-white'
                      }`}>{r.status}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {r.corridor && <span>{r.corridor} &middot; </span>}
                      {r.start_date && <span>{new Date(r.start_date + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} &middot; </span>}
                      <span>{r.maxDay} day{r.maxDay !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Target day */}
            {selectedRouteId && (
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Place on Day</label>
                <input
                  type="number"
                  min="1"
                  value={targetDay}
                  onChange={(e) => setTargetDay(parseInt(e.target.value) || 1)}
                  className="w-24 px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                <span className="text-xs text-gray-400 ml-2">
                  {selectedRoute?.maxDay > 0
                    ? `(Route has ${selectedRoute.maxDay} day${selectedRoute.maxDay !== 1 ? 's' : ''}, append = Day ${selectedRoute.maxDay + 1})`
                    : '(Route is empty)'}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-5 pt-3 border-t border-gray-500/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={!selectedRouteId || moving}
            className="px-4 py-2 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {moving ? 'Moving...' : 'Move Jobs'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveDayModal;
